import { getAnalyticsOverviewSections } from "./analyticsOverviewConfig";
import { overviewThumbForSection } from "../overviewThumbs/overviewThumbRegistry";

const SECTION_BLURBS = {
  performance: "Dashboards and KPI snapshots for your network or salon.",
  skin: "Funnel, services, and network skin analysis metrics.",
  hair: "Hair analysis funnel and service performance.",
  operations: "Check-in queue, guests, flow analytics, and customer engagement.",
  engagement: "WhatsApp AI, appointments, leads, and campaign automation.",
  reels: "Transformation reels library and the Naturals Reels capture app.",
  staff: "Team roster, availability, slots, and stylist analytics.",
  store: "Price list, products, and store apps.",
  administration: "Franchise users, regions, licenses, and HQ inbox.",
  setup: "Skin analysis settings, service lists, and product catalog.",
  kiosk: "Registration forms, themes, and kiosk geofence.",
};

export function buildOverviewGates(permissionService) {
  return {
    canViewStoreKpis: () => permissionService.canViewStoreKpis(),
    canManageSkinSettings: () => permissionService.canManageSkinSettings(),
    canManageServices: () => permissionService.canManageServices(),
    canManageProducts: () => permissionService.canManageProducts(),
    isFranchise: () => permissionService.isFranchise(),
    isDashboardStaff: () => permissionService.isDashboardStaff?.(),
    canManageDashboardStaff: () => permissionService.canManageDashboardStaff?.(),
    canCreateFranchiseUsers: () => permissionService.canCreateFranchiseUsers?.(),
    canViewComplaints: () => permissionService.canViewComplaints?.(),
    canViewContactMessages: () => permissionService.canViewContactMessages?.(),
    isOrganizationScope: () => permissionService.isOrganizationScope?.() ?? false,
    canEditPageSetup: () =>
      permissionService.canEditSetFlow?.() || permissionService.canEditOrgPageSetup?.(),
    canManageKiosk: () => permissionService.canManageKioskLandings?.(),
    isAdmin: () => permissionService.isAdmin?.(),
    hasOrgService: (key) =>
      typeof permissionService.hasOrgService === "function"
        ? permissionService.hasOrgService(key)
        : true,
    orgEnabledServices: () =>
      typeof permissionService.getOrgEnabledServices === "function"
        ? permissionService.getOrgEnabledServices()
        : [],
  };
}

export function getOverviewSections(portal, permissionService) {
  const gates =
    permissionService != null
      ? buildOverviewGates(permissionService)
      : { hasOrgService: () => true };
  return getAnalyticsOverviewSections(portal, gates);
}

export function overviewSectionEntryPath(portal, section) {
  const base =
    portal === "workspace"
      ? "/naturals/workspace"
      : portal === "executive"
        ? "/naturals/executive"
        : "/naturals/partner";
  const first = section?.cards?.[0]?.path;
  return first || `${base}/overview/${section.id}`;
}

export function getOverviewTopLevelCards(portal, permissionService) {
  const sections = getOverviewSections(portal, permissionService);
  return sections.map((section, index) => ({
    id: `hub-${section.id}`,
    title: section.title,
    image: overviewThumbForSection(section.id),
    description: SECTION_BLURBS[section.id] || section.cards[0]?.description || "Open section",
    path: overviewSectionEntryPath(portal, section),
  }));
}

export function findOverviewSection(portal, permissionService, sectionId) {
  return getOverviewSections(portal, permissionService).find((s) => s.id === sectionId) || null;
}

function portalBase(portal) {
  if (portal === "workspace") return "/naturals/workspace";
  if (portal === "executive") return "/naturals/executive";
  return "/naturals/partner";
}

function normalizePath(path) {
  return String(path || "").replace(/\/$/, "");
}

export function pathBelongsToOverviewRoute(pathname, routePath) {
  const p = normalizePath(pathname);
  const target = normalizePath(routePath);
  if (!target) return false;
  return p === target || p.startsWith(`${target}/`);
}

/** Extra paths that should highlight the same sidebar item as the card target. */
const CARD_ACTIVE_ALIASES = {
  servicelist: ["add-service", "edit-service", "view-service"],
  staff: ["staff-availability"],
  "staff-availability": ["staff"],
  "check-ins": ["operations/appointments", "guest/appointments"],
  "operations/appointments": ["guest/appointments", "engagement/appointments"],
  "guest/appointments": ["operations/appointments", "engagement/appointments"],
  engagement: [
    "engagement/leads",
    "engagement/appointments",
    "engagement/ai-docs",
    "engagement/automations",
    "engagement/conversations",
    "engagement/human-intervention",
    "engagement/call-records",
    "engagement/voice-call",
  ],
};

export function activeMatchPathsForCard(portal, card) {
  const base = portalBase(portal);
  const paths = [card.path];
  const rel = card.path.replace(base, "");
  const relKey = rel.replace(/^\//, "").replace(/\/+$/, "");
  const aliases = CARD_ACTIVE_ALIASES[relKey] || [];
  aliases.forEach((seg) => paths.push(`${base}/${seg}`));
  return paths;
}

/** Which overview section (hub) the current URL belongs to, if any. */
export function resolveActiveOverviewSection(portal, pathname, permissionService) {
  const base = portalBase(portal);
  const p = normalizePath(pathname);

  if (p === `${base}/my-orders`) {
    return null;
  }

  const hubPrefix = `${base}/overview/`;
  if (p.startsWith(hubPrefix)) {
    const sectionId = p.slice(hubPrefix.length).split("/")[0];
    if (sectionId) {
      return findOverviewSection(portal, permissionService, sectionId);
    }
  }

  const sections = getOverviewSections(portal, permissionService);
  for (const section of sections) {
    for (const card of section.cards) {
      if (pathBelongsToOverviewRoute(p, card.path)) {
        return section;
      }
    }
  }
  return null;
}

export function overviewSectionSidebarId(sectionId) {
  if (sectionId === "setup") return "catalog-setup";
  return sectionId;
}
